import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ComplianceRequirements} from '@gatewee/types/domain-setting';
import {
  Onboarding,
  OnboardingFileUpload,
  OnboardingMetadata,
} from '@gatewee/types/onboarding';
import {LegalRepresentative} from '@gatewee/types/person';
import {Address} from '@gatewee/types/client';

export interface OnboardingFlowForms {
  leadFormGroup: FormGroup;
  businessFormGroup: FormGroup;
  addressFormGroup: FormGroup;
  estimatedVolumesFormGroup: FormGroup;
  businessProfileFormGroup: FormGroup;
  partnersFormGroup: FormArray;
  documentsFormGroup: FormArray;
}

export interface BuildOnboardingPayloadOptions {
  partialData?: Onboarding;
  includeLiveFiles?: boolean;
}

export function buildProfileFormArrays(
  fb: FormBuilder,
  metadata?: OnboardingMetadata,
): {
  operatingRegions: FormArray;
  organizationalTools: FormArray;
} {
  const operatingRegions = fb.array([], Validators.required);
  const organizationalTools = fb.array([], Validators.required);

  for (const region of metadata?.operatingRegions ?? []) {
    operatingRegions.push(fb.control(region));
  }
  for (const tool of metadata?.organizationalTools ?? []) {
    organizationalTools.push(fb.control(tool));
  }

  return {operatingRegions, organizationalTools};
}

export function hydrateDocumentsFormArray(
  fb: FormBuilder,
  files: OnboardingFileUpload[] | undefined,
  requiredDocuments: ComplianceRequirements,
): FormArray {
  const arr = fb.array<FormGroup>([]);
  const fileByType = new Map((files ?? []).map(file => [file.type, file]));
  const addedTypes = new Set<string>();

  for (const [documentType, isRequired] of Object.entries(requiredDocuments)) {
    if (!isRequired) {
      continue;
    }

    const existing = fileByType.get(documentType);
    arr.push(
      fb.group({
        type: [documentType, Validators.required],
        base64: [existing?.base64 ?? null, Validators.required],
      }),
    );
    addedTypes.add(documentType);
  }

  for (const file of files ?? []) {
    if (addedTypes.has(file.type)) {
      continue;
    }
    arr.push(
      fb.group({
        type: [file.type, Validators.required],
        base64: [file.base64 ?? null],
      }),
    );
  }

  return arr;
}

export function areAllRequiredDocumentsUploaded(
  documentsFormGroup: FormArray,
  requiredDocuments: ComplianceRequirements,
): boolean {
  for (const [documentType, isRequired] of Object.entries(requiredDocuments)) {
    if (!isRequired) {
      continue;
    }
    const control = documentsFormGroup.controls.find(
      c => c.get('type')?.value === documentType,
    );
    if (!control?.get('base64')?.value) {
      return false;
    }
  }
  return true;
}

export function isStepValid(
  stepIndex: number,
  forms: OnboardingFlowForms,
  requiredDocuments: ComplianceRequirements,
): boolean {
  switch (stepIndex) {
    case 0:
      return forms.leadFormGroup.valid;
    case 1:
      return forms.businessFormGroup.valid;
    case 2:
      return forms.addressFormGroup.valid;
    case 3:
      return forms.partnersFormGroup.valid;
    case 4:
      return forms.estimatedVolumesFormGroup.valid;
    case 5:
      return forms.businessProfileFormGroup.valid;
    case 6:
      return (
        forms.documentsFormGroup.valid &&
        areAllRequiredDocumentsUploaded(
          forms.documentsFormGroup,
          requiredDocuments,
        )
      );
    default:
      return false;
  }
}

export function markStepTouched(
  stepIndex: number,
  forms: OnboardingFlowForms,
): void {
  const control = getStepControl(stepIndex, forms);
  if (control instanceof FormGroup || control instanceof FormArray) {
    control.markAllAsTouched();
  }
}

function getStepControl(
  stepIndex: number,
  forms: OnboardingFlowForms,
): FormGroup | FormArray | null {
  switch (stepIndex) {
    case 0:
      return forms.leadFormGroup;
    case 1:
      return forms.businessFormGroup;
    case 2:
      return forms.addressFormGroup;
    case 3:
      return forms.partnersFormGroup;
    case 4:
      return forms.estimatedVolumesFormGroup;
    case 5:
      return forms.businessProfileFormGroup;
    case 6:
      return forms.documentsFormGroup;
    default:
      return null;
  }
}

function hasRequiredDocuments(
  data: Onboarding,
  requiredDocuments: ComplianceRequirements,
): boolean {
  for (const [documentType, isRequired] of Object.entries(requiredDocuments)) {
    if (!isRequired) {
      continue;
    }
    const file = data.files?.find(entry => entry.type === documentType);
    if (!file?.base64) {
      return false;
    }
  }
  return true;
}

export function computeResumeStepIndex(
  data: Onboarding,
  requiredDocuments: ComplianceRequirements,
): number {
  const metadata = data.metadata;

  if (
    !data.representative?.email ||
    !data.representative?.name ||
    !data.representative?.document
  ) {
    return 0;
  }

  if (
    !data.document ||
    !data.name ||
    !data.displayName ||
    !metadata?.companyType
  ) {
    return 1;
  }

  if (!data.address?.postalCode) {
    return 2;
  }

  if (!data.partners?.length) {
    return 3;
  }

  if (!metadata.estimatedMonthlyVolume || !metadata.estimatedAverageTicket) {
    return 4;
  }

  if (
    !metadata.clientType ||
    !metadata.operatingRegions?.length ||
    !metadata.organizationalTools?.length
  ) {
    return 5;
  }

  if (!hasRequiredDocuments(data, requiredDocuments)) {
    return 6;
  }

  return 6;
}

function hasText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasNonemptyAddress(address: Address | undefined): boolean {
  if (!address) {
    return false;
  }
  const fields: (keyof Address)[] = [
    'street',
    'number',
    'complement',
    'neighborhood',
    'city',
    'state',
    'postalCode',
  ];
  return fields.some(field => hasText(address[field]));
}

export function sanitizePartnerForPayload(
  partner: LegalRepresentative,
): LegalRepresentative {
  if (!partner.address || hasNonemptyAddress(partner.address)) {
    return partner;
  }
  const rest = {...partner};
  delete rest.address;
  return rest;
}

function hasItems<T>(value: T[] | undefined | null): value is T[] {
  return Array.isArray(value) && value.length > 0;
}

function isValidIso8601Date(value: unknown): boolean {
  if (!hasText(value)) {
    return false;
  }
  const date = new Date(String(value));
  return !Number.isNaN(date.getTime());
}

function isNonemptyPlainObject(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return Object.keys(value as Record<string, unknown>).length > 0;
}

function buildMetadataPayload(
  forms: OnboardingFlowForms,
  partialData?: Onboarding,
): OnboardingMetadata | undefined {
  const volumesValue = forms.estimatedVolumesFormGroup.value as {
    estimatedMonthlyVolume?: string;
    estimatedAverageTicket?: string;
  };
  const profileValue = forms.businessProfileFormGroup.value as {
    clientType?: string;
    operatingRegions?: string[];
    organizationalTools?: string[];
  };
  const businessAreas = forms.businessFormGroup.get('businessAreas')?.value as
    | string[]
    | undefined;
  const companyType = forms.businessFormGroup.get('companyType')?.value as
    | string
    | undefined;

  const metadata: Partial<OnboardingMetadata> = {};

  if (hasText(companyType)) {
    metadata.companyType = companyType!.trim();
  }
  if (hasText(profileValue.clientType)) {
    metadata.clientType = profileValue.clientType!.trim();
  }
  if (hasText(volumesValue.estimatedAverageTicket)) {
    metadata.estimatedAverageTicket =
      volumesValue.estimatedAverageTicket!.trim();
  }
  if (hasText(volumesValue.estimatedMonthlyVolume)) {
    metadata.estimatedMonthlyVolume =
      volumesValue.estimatedMonthlyVolume!.trim();
  }
  if (hasItems(businessAreas)) {
    metadata.businessAreas = businessAreas;
  }
  if (hasItems(profileValue.operatingRegions)) {
    metadata.operatingRegions = profileValue.operatingRegions;
  }
  if (hasItems(profileValue.organizationalTools)) {
    metadata.organizationalTools = profileValue.organizationalTools;
  }

  const providerRequest = partialData?.metadata?.providerRequest;
  if (isNonemptyPlainObject(providerRequest)) {
    metadata.providerRequest = providerRequest!;
  }

  return Object.keys(metadata).length > 0
    ? (metadata as OnboardingMetadata)
    : undefined;
}

function buildAddressPayload(
  forms: OnboardingFlowForms,
): Onboarding['address'] | undefined {
  const address = forms.addressFormGroup.value as Onboarding['address'];
  if (!hasText(address?.postalCode)) {
    return undefined;
  }
  return address;
}

function buildFilesPayload(
  forms: OnboardingFlowForms,
  options: {partialData?: Onboarding; includeLiveFiles?: boolean},
): OnboardingFileUpload[] | undefined {
  const rawFiles = options.includeLiveFiles
    ? (forms.documentsFormGroup.value as OnboardingFileUpload[])
    : options.partialData?.files;

  const files = (rawFiles ?? []).filter(file => hasText(file?.base64));
  return hasItems(files) ? files : undefined;
}

export function buildOnboardingPayload(
  forms: OnboardingFlowForms,
  options: BuildOnboardingPayloadOptions = {},
): Partial<Onboarding> {
  const {partialData, includeLiveFiles = false} = options;
  const leadValue = forms.leadFormGroup.value as LegalRepresentative & {
    acceptTerms?: boolean;
  };
  const volumesValue = forms.estimatedVolumesFormGroup.value as {
    website?: string;
    noWebsite?: boolean;
  };

  const representative: LegalRepresentative = {
    name: leadValue.name,
    document: leadValue.document,
    phone: leadValue.phone,
    email: leadValue.email,
  };

  const payload: Partial<Onboarding> = {
    name: forms.businessFormGroup.get('name')?.value,
    document: forms.businessFormGroup.get('document')?.value,
    displayName: forms.businessFormGroup.get('displayName')?.value,
    representative,
  };

  if (!volumesValue.noWebsite) {
    const website =
      volumesValue.website?.trim() || partialData?.website?.trim() || '';
    if (hasText(website)) {
      payload.website = website;
    }
  }

  const metadata = buildMetadataPayload(forms, partialData);
  if (metadata) {
    payload.metadata = metadata;
  }

  const address = buildAddressPayload(forms);
  if (address) {
    payload.address = address;
  }

  const partners = forms.partnersFormGroup.value as LegalRepresentative[];
  if (hasItems(partners)) {
    payload.partners = partners.map(sanitizePartnerForPayload);
  }

  const officialData = forms.businessFormGroup.get('officialData')?.value;
  if (isNonemptyPlainObject(officialData)) {
    payload.officialData = officialData;
  }

  const files = buildFilesPayload(forms, {partialData, includeLiveFiles});
  if (files) {
    payload.files = files;
  }

  if (isValidIso8601Date(partialData?.foundationDate)) {
    payload.foundationDate = String(partialData!.foundationDate);
  }

  if (partialData?.id) {
    payload.id = partialData.id;
  }

  return payload;
}
